import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API_BASE } from "../config";
import { useSummit } from "../pages/summitdetails";
import {
  RiFileTextLine,
  RiCheckLine,
  RiPhoneLine,
  RiMailLine,
  RiArrowRightLine,
  RiDownloadLine,
  RiPrinterLine,
  RiMenuLine,
  RiCloseLine,
} from "react-icons/ri";
import toast from "react-hot-toast";

/* ─── THEME COLORS ───────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
  gradTealDark: "linear-gradient(135deg, #1F7A6E 0%, #2A9D8F 100%)",
  gradAccent: "linear-gradient(135deg, #3BBCAE 0%, #2A9D8F 100%)",
  gradSuccess: "linear-gradient(135deg, #2A9D8F 0%, #6BC2B5 100%)",
};

/* ─── CSS ──────────────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth;-webkit-font-smoothing:antialiased}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  ::-webkit-scrollbar{width:6px}
  ::-webkit-scrollbar-track{background:${T.borderLight}}
  ::-webkit-scrollbar-thumb{background:${T.primaryLight};border-radius:4px}

  .t-hero{background:${T.gradSoft};border-bottom:1px solid ${T.border};padding:clamp(32px,6vw,80px) clamp(16px,4vw,64px) clamp(32px,5vw,64px);position:relative;overflow:hidden}
  .t-hero::before{content:'';position:absolute;top:-120px;left:50%;transform:translateX(-50%);width:600px;height:600px;background:radial-gradient(circle,rgba(42,157,143,0.08) 0%,transparent 70%);pointer-events:none}
  .t-hero-i{max-width:1320px;margin:0 auto;display:flex;align-items:flex-start;justify-content:space-between;gap:24px;flex-wrap:wrap;position:relative}
  .t-hero-lbl{display:inline-flex;align-items:center;gap:6px;padding:4px 12px;border-radius:20px;background:${T.softTeal};border:1px solid ${T.border};color:${T.primary};font-size:clamp(10px,0.8vw,11px);font-weight:700;letter-spacing:.08em;text-transform:uppercase;margin-bottom:clamp(12px,2vw,20px)}
  .t-hero-h{font-family:'Playfair Display',serif;font-size:clamp(1.6rem,4.5vw,3.4rem);font-weight:900;color:${T.navy};letter-spacing:-.03em;line-height:1.06;margin-bottom:clamp(10px,1.5vw,14px)}
  .t-hero-h em{font-style:normal;color:${T.accentGold}}
  .t-hero-p{font-size:clamp(0.85rem,1.3vw,1rem);color:${T.textMid};line-height:1.7;max-width:500px}
  .t-stats{display:flex;gap:1px;background:${T.border};border-radius:12px;border:1px solid ${T.border};overflow:hidden;align-self:flex-start;flex-shrink:0;flex-wrap:wrap}
  .t-st{background:${T.white};padding:clamp(14px,2vw,20px) clamp(18px,3vw,28px);display:flex;flex-direction:column;gap:4px;min-width:70px;flex:1}
  .t-st:nth-child(2){background:${T.offWhite}}
  .t-st-n{font-family:'Playfair Display',serif;font-size:clamp(1.4rem,2.2vw,1.8rem);font-weight:900;color:${T.accentGold};line-height:1;letter-spacing:-.03em}
  .t-st-l{font-size:clamp(9px,0.7vw,11px);color:${T.textMuted};font-weight:500;text-transform:uppercase;letter-spacing:.06em}

  .t-guidelines-wrapper{padding:clamp(24px,4vw,40px) clamp(12px,3vw,64px) clamp(32px,5vw,56px);background:${T.offWhite}}
  .t-guidelines-container{max-width:1320px;margin:0 auto}
  
  .t-sidebar{background:${T.white};border-radius:16px;border:1px solid ${T.borderLight};padding:clamp(16px,2.5vw,24px);box-shadow:${T.shadow};position:sticky;top:24px}
  .t-sidebar-title{font-size:clamp(16px,1.4vw,18px);font-weight:700;color:${T.navy};margin-bottom:clamp(12px,1.5vw,16px);display:flex;align-items:center;gap:8px}
  .t-sidebar-nav{list-style:none;margin:0;padding:0}
  .t-sidebar-nav-item{width:100%;text-align:left;padding:clamp(8px,1vw,10px) clamp(10px,1.5vw,14px);border-radius:10px;font-size:clamp(13px,1.1vw,14px);color:${T.textMid};transition:all 0.2s;cursor:pointer;display:flex;align-items:center;gap:8px;background:none;border:none;font-family:'Inter',sans-serif;line-height:1.4}
  .t-sidebar-nav-item:hover{background:${T.softTeal};transform:translateX(4px);color:${T.primary}}
  .t-sidebar-nav-item.active{background:${T.softTeal};color:${T.primary};font-weight:500}
  
  .t-guideline-card{background:${T.white};border-radius:16px;border:1px solid ${T.borderLight};overflow:hidden;box-shadow:${T.shadow};transition:all 0.3s ease;margin-bottom:clamp(14px,2vw,20px);cursor:pointer}
  .t-guideline-card:hover{box-shadow:${T.shadowMd};border-color:${T.primaryLight}}
  .t-guideline-header{display:flex;align-items:flex-start;gap:clamp(12px,1.5vw,16px);padding:clamp(16px,2.5vw,24px)}
  .t-guideline-icon{width:clamp(44px,5vw,52px);height:clamp(44px,5vw,52px);border-radius:14px;background:${T.softTeal};display:flex;align-items:center;justify-content:center;font-size:clamp(20px,2vw,24px);flex-shrink:0}
  .t-guideline-title{font-size:clamp(16px,1.4vw,18px);font-weight:700;color:${T.text};margin-bottom:4px}
  .t-guideline-order{font-size:clamp(10px,0.8vw,11px);padding:2px 8px;border-radius:20px;background:${T.softTeal};color:${T.primary};white-space:nowrap}
  .t-guideline-desc{font-size:clamp(13px,1.1vw,14px);color:${T.textMuted};line-height:1.65;margin-top:6px}
  .t-guideline-body{padding:0 clamp(16px,2.5vw,24px) clamp(16px,2.5vw,24px) clamp(16px,2.5vw,92px)}
  .t-contact-card{background:${T.gradPrimary};border-radius:16px;padding:clamp(24px,3vw,32px);text-align:center;margin-top:clamp(24px,3vw,32px)}
  .t-footer-note{text-align:center;padding-top:clamp(24px,3vw,32px);margin-top:clamp(24px,3vw,32px);border-top:1px solid ${T.borderLight}}

  .t-action-buttons{display:flex;flex-wrap:wrap;justify-content:center;gap:clamp(8px,1vw,12px);margin-top:clamp(16px,2vw,24px);padding:0 clamp(12px,4vw,20px)}
  .t-btn{display:inline-flex;align-items:center;gap:6px;padding:clamp(8px,1vw,10px) clamp(16px,2vw,24px);border-radius:40px;font-size:clamp(12px,1.1vw,14px);font-weight:600;cursor:pointer;transition:all 0.2s;border:none;font-family:'Inter',sans-serif;white-space:nowrap}
  .t-btn-primary{background:${T.white};color:${T.primary};border:1px solid ${T.border}}
  .t-btn-primary:hover{transform:scale(1.05);box-shadow:${T.shadowMd};border-color:${T.primary}}
  .t-copy-btn{font-size:clamp(11px,0.9vw,12px);padding:clamp(4px,0.5vw,6px) clamp(12px,1.5vw,16px);border-radius:20px;background:${T.offWhite};color:${T.primary};border:none;cursor:pointer;transition:all 0.2s;font-family:'Inter',sans-serif}
  .t-copy-btn:hover{background:${T.softTeal}}

  .t-gl-grid{display:grid;gap:clamp(20px,3vw,24px);grid-template-columns:1fr}
  @media(min-width:1024px){.t-gl-grid{grid-template-columns:1fr 2fr}}

  /* Mobile Sidebar Toggle */
  .t-sidebar-toggle{display:none;width:100%;padding:clamp(10px,1.5vw,12px);background:${T.white};border:1px solid ${T.border};border-radius:10px;font-size:clamp(13px,1.1vw,14px);font-weight:600;color:${T.text};cursor:pointer;font-family:'Inter',sans-serif;align-items:center;justify-content:space-between;margin-bottom:16px}
  .t-sidebar-toggle:hover{background:${T.softTeal}}
  .t-sidebar-mobile{display:block}
  @media(max-width:768px){
    .t-sidebar-toggle{display:flex}
    .t-sidebar-mobile{display:none}
    .t-sidebar-mobile.open{display:block}
    .t-sidebar{position:relative;top:0;margin-bottom:16px}
  }

  .rotate-90{transform:rotate(90deg)}
  .transition-transform{transition:transform 0.3s ease}

  .t-sw{max-width:1320px;margin:0 auto;display:flex;flex-direction:column;align-items:center;text-align:center;padding:clamp(40px,6vw,72px) clamp(16px,4vw,24px);gap:10px}
  .t-s-icon{width:clamp(44px,5vw,52px);height:clamp(44px,5vw,52px);border-radius:12px;background:${T.softTeal};border:1px solid ${T.border};display:flex;align-items:center;justify-content:center;font-size:clamp(18px,2vw,22px);color:${T.primary}}
  .t-s-title{font-family:'Playfair Display',serif;font-size:clamp(18px,2vw,20px);font-weight:800;color:${T.navy}}
  .t-s-sub{font-size:clamp(13px,1.2vw,14px);color:${T.textMuted};max-width:300px;line-height:1.6}
  .t-s-btn{margin-top:4px;padding:8px 18px;border-radius:8px;border:1px solid ${T.border};background:${T.white};color:${T.text};font-family:'Inter',sans-serif;font-size:clamp(12px,1vw,13px);font-weight:500;cursor:pointer;transition:all .15s}
  .t-s-btn:hover{border-color:${T.primary};color:${T.primary}}

  /* Responsive overrides */
  @media(max-width:768px){
    .t-hero-i{flex-direction:column;align-items:stretch}
    .t-stats{width:100%}
    .t-guideline-header{flex-wrap:wrap}
    .t-guideline-body{padding:0 14px 14px 14px}
    .t-contact-card{padding:20px}
    .t-contact-card .t-contact-items{flex-direction:column;gap:12px}
  }

  @media(max-width:480px){
    .t-hero{padding:20px 12px 24px}
    .t-hero-h{font-size:1.4rem}
    .t-hero-p{font-size:0.85rem}
    .t-stats{flex-direction:column}
    .t-st{padding:10px 14px;min-width:auto}
    .t-st-n{font-size:1.2rem}
    .t-action-buttons{gap:6px}
    .t-btn{font-size:12px;padding:6px 14px}
    .t-guideline-header{padding:14px;gap:10px}
    .t-guideline-icon{width:38px;height:38px;font-size:18px}
    .t-guideline-title{font-size:15px}
    .t-guideline-desc{font-size:13px}
    .t-sidebar{padding:14px}
    .t-sidebar-title{font-size:15px}
    .t-sidebar-nav-item{font-size:12px;padding:6px 10px}
  }

  @media(max-width:360px){
    .t-hero-h{font-size:1.2rem}
    .t-guideline-title{font-size:14px}
    .t-guideline-desc{font-size:12px}
    .t-btn{font-size:11px;padding:4px 10px}
  }

  @keyframes spin{to{transform:rotate(360deg)}}
`;

/* ════════════════════════════════════════════════════════════════
   COMPONENT
════════════════════════════════════════════════════════════════ */
const SubmissionGuidelines = () => {
  // ✅ Use context to get the real MongoDB _id
  const { eventId } = useSummit();

  const [guidelines, setGuidelines] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedGuideline, setExpandedGuideline] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (eventId) {
      fetchActiveGuidelines();
    } else {
      setError("No event specified");
      setLoading(false);
    }
  }, [eventId]);

  const fetchActiveGuidelines = async () => {
    try {
      setLoading(true);
      setError(null);

      const url = `${API_BASE}/api/submission-guidelines/active/${eventId}`;
      console.log("Fetching guidelines from:", url);

      const response = await axios.get(url);

      if (response.data.success && response.data.data) {
        setGuidelines(response.data.data);
        setError(null);
      } else {
        setError(response.data.message || "No active guidelines found for this event");
      }
    } catch (err) {
      console.error("Error fetching guidelines:", err);
      if (err.response?.status === 404) {
        setError("Submission guidelines not available for this event yet.");
      } else {
        setError(err.response?.data?.message || "Failed to load submission guidelines");
      }
      toast.error("Failed to load submission guidelines");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${guidelines?.title || "Submission Guidelines"}</title>
        <meta charset="UTF-8">
        <style>
          *{margin:0;padding:0;box-sizing:border-box}
          body{font-family:'Segoe UI','Inter',-apple-system,BlinkMacSystemFont,sans-serif;max-width:900px;margin:0 auto;padding:40px;color:#1C3461;line-height:1.6}
          h1{color:#2A9D8F;border-bottom:3px solid #2A9D8F;padding-bottom:12px;margin-bottom:20px;font-size:28px}
          .description{font-size:16px;color:#2D4A5A;margin-bottom:30px;line-height:1.7}
          .guideline-item{margin-bottom:24px;padding:20px;background:#E8F5F3;border-radius:12px;border-left:4px solid #2A9D8F;page-break-inside:avoid;break-inside:avoid}
          .guideline-icon{font-size:28px;margin-bottom:12px}
          .guideline-title{font-size:18px;font-weight:700;color:#1A3A4A;margin-bottom:8px}
          .guideline-order{display:inline-block;background:#C8E8E4;padding:2px 10px;border-radius:20px;font-size:11px;color:#1F7A6E;margin-left:10px}
          .guideline-desc{font-size:14px;color:#2D4A5A;line-height:1.6}
          .footer{margin-top:50px;padding-top:20px;border-top:1px solid #C8E8E4;text-align:center;color:#6B8090;font-size:12px}
          .contact-section{margin-top:40px;padding:20px;background:#2A9D8F;color:white;border-radius:12px;text-align:center}
          .contact-section h3{margin-bottom:10px}
          @media print{body{padding:20px}.guideline-item{break-inside:avoid;page-break-inside:avoid}}
        </style>
      </head>
      <body>
        <h1>${guidelines?.title || "Submission Guidelines"}</h1>
        <div class="description">${guidelines?.description || ""}</div>
        ${(guidelines?.guidelines || [])
          .sort((a, b) => a.order - b.order)
          .map(item => `
          <div class="guideline-item">
            <div class="guideline-icon">${item.icon || "📝"}</div>
            <div class="guideline-title">${item.title}<span class="guideline-order">#${item.order}</span></div>
            <div class="guideline-desc">${item.description}</div>
          </div>
        `).join('')}
        <div class="contact-section">
          <h3>Need Help?</h3>
          <p>Email: submissions@meetings21.com | Phone: +1 (555) 123-4567</p>
        </div>
        <div class="footer">
          <p>Generated on ${new Date().toLocaleDateString()} | Version: ${guidelines?.version || "1.0"}</p>
          <p>© ${new Date().getFullYear()} All Rights Reserved</p>
        </div>
      </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
    toast.success("Print window opened!");
  };

  const handleDownload = () => {
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${guidelines?.title || "Submission Guidelines"}</title>
        <meta charset="UTF-8">
        <style>
          *{margin:0;padding:0;box-sizing:border-box}
          body{font-family:'Segoe UI','Inter',-apple-system,BlinkMacSystemFont,sans-serif;max-width:900px;margin:0 auto;padding:40px;color:#1C3461;line-height:1.6}
          h1{color:#2A9D8F;border-bottom:3px solid #2A9D8F;padding-bottom:12px;margin-bottom:20px;font-size:28px}
          .description{font-size:16px;color:#2D4A5A;margin-bottom:30px;line-height:1.7}
          .guideline-item{margin-bottom:24px;padding:20px;background:#E8F5F3;border-radius:12px;border-left:4px solid #2A9D8F}
          .guideline-icon{font-size:28px;margin-bottom:12px}
          .guideline-title{font-size:18px;font-weight:700;color:#1A3A4A;margin-bottom:8px}
          .guideline-order{display:inline-block;background:#C8E8E4;padding:2px 10px;border-radius:20px;font-size:11px;color:#1F7A6E;margin-left:10px}
          .guideline-desc{font-size:14px;color:#2D4A5A;line-height:1.6}
          .footer{margin-top:50px;padding-top:20px;border-top:1px solid #C8E8E4;text-align:center;color:#6B8090;font-size:12px}
          .contact-section{margin-top:40px;padding:20px;background:#2A9D8F;color:white;border-radius:12px;text-align:center}
          .contact-section h3{margin-bottom:10px}
        </style>
      </head>
      <body>
        <h1>${guidelines?.title || "Submission Guidelines"}</h1>
        <div class="description">${guidelines?.description || ""}</div>
        ${(guidelines?.guidelines || [])
          .sort((a, b) => a.order - b.order)
          .map(item => `
          <div class="guideline-item">
            <div class="guideline-icon">${item.icon || "📝"}</div>
            <div class="guideline-title">${item.title}<span class="guideline-order">#${item.order}</span></div>
            <div class="guideline-desc">${item.description}</div>
          </div>
        `).join('')}
        <div class="contact-section">
          <h3>Need Help?</h3>
          <p>Email: submissions@meetings21.com | Phone: +1 (555) 123-4567</p>
        </div>
        <div class="footer">
          <p>Generated on ${new Date().toLocaleDateString()} | Version: ${guidelines?.version || "1.0"}</p>
          <p>© ${new Date().getFullYear()} All Rights Reserved</p>
        </div>
      </body>
      </html>
    `;
    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${guidelines?.title || "submission-guidelines"}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Guidelines downloaded!");
  };

  /* ── Loading state ── */
  if (loading) {
    return (
      <div style={{ background: T.offWhite, minHeight: "100vh" }}>
        <style>{css}</style>
        <div className="t-hero">
          <div className="t-hero-i">
            <div>
              <div className="t-hero-lbl"><RiFileTextLine size={12} /> Meetings21 · Author Information</div>
              <h1 className="t-hero-h">Submission <em>Guidelines</em></h1>
              <p className="t-hero-p">Loading guidelines...</p>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 18 }}>
                <div style={{ flex: 1, height: 2, maxWidth: 60, background: `linear-gradient(90deg,transparent,${T.primary}40,transparent)` }} />
                <div style={{ width: 6, height: 6, background: T.accentGold, transform: "rotate(45deg)" }} />
                <div style={{ flex: 1, height: 2, maxWidth: 60, background: `linear-gradient(90deg,transparent,${T.primary}40,transparent)` }} />
              </div>
            </div>
          </div>
        </div>
        <div className="t-guidelines-wrapper">
          <div style={{ textAlign: "center", padding: "clamp(40px,6vw,60px) 20px" }}>
            <div style={{ width: 40, height: 40, border: `3px solid ${T.border}`, borderTopColor: T.primary, borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 16px" }} />
            <p style={{ color: T.textMuted, fontSize: 'clamp(14px,1.2vw,15px)' }}>Loading submission guidelines...</p>
          </div>
        </div>
      </div>
    );
  }

  /* ── Error / empty state ── */
  if (error || !guidelines) {
    return (
      <div style={{ background: T.offWhite, minHeight: "100vh" }}>
        <style>{css}</style>
        <div className="t-hero">
          <div className="t-hero-i">
            <div>
              <div className="t-hero-lbl"><RiFileTextLine size={12} /> Meeting21 · Author Information</div>
              <h1 className="t-hero-h">Submission <em>Guidelines</em></h1>
              <p className="t-hero-p">Essential information for authors submitting to Meeting21</p>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 18 }}>
                <div style={{ flex: 1, height: 2, maxWidth: 60, background: `linear-gradient(90deg,transparent,${T.primary}40,transparent)` }} />
                <div style={{ width: 6, height: 6, background: T.accentGold, transform: "rotate(45deg)" }} />
                <div style={{ flex: 1, height: 2, maxWidth: 60, background: `linear-gradient(90deg,transparent,${T.primary}40,transparent)` }} />
              </div>
            </div>
          </div>
        </div>
        <div className="t-sw">
          <div className="t-s-icon"><RiFileTextLine size={22} /></div>
          <p className="t-s-title">No Guidelines Available</p>
          <p className="t-s-sub">{error || "Submission guidelines will be available soon."}</p>
          <button className="t-s-btn" onClick={fetchActiveGuidelines}>Refresh</button>
        </div>
      </div>
    );
  }

  const sortedGuidelines = [...(guidelines.guidelines || [])].sort((a, b) => a.order - b.order);
  const totalGuidelines = sortedGuidelines.length;

  /* ── Main render ── */
  return (
    <div style={{ background: T.offWhite, minHeight: "100vh" }}>
      <style>{css}</style>

      {/* ── HERO ── */}
      <section className="t-hero">
        <div className="t-hero-i">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="t-hero-lbl">
              <RiFileTextLine size={12} />
              Meetings21 · Author Information
            </div>
            <h1 className="t-hero-h">Submission <em>Guidelines</em></h1>
            <p className="t-hero-p">
              Essential information for authors submitting to Meetings21. Please review these
              guidelines carefully before preparing your submission.
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 18 }}>
              <div style={{ flex: 1, height: 2, maxWidth: 60, background: `linear-gradient(90deg,transparent,${T.primary}40,transparent)` }} />
              <div style={{ width: 6, height: 6, background: T.accentGold, transform: "rotate(45deg)" }} />
              <div style={{ flex: 1, height: 2, maxWidth: 60, background: `linear-gradient(90deg,transparent,${T.primary}40,transparent)` }} />
            </div>
          </div>
          <div className="t-stats">
            <div className="t-st">
              <span className="t-st-n">{totalGuidelines}</span>
              <span className="t-st-l">Guidelines</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">{guidelines.version || "1.0"}</span>
              <span className="t-st-l">Version</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">2025</span>
              <span className="t-st-l">Edition</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Action Buttons ── */}
      <div className="t-action-buttons">
        <button onClick={handleDownload} className="t-btn t-btn-primary">
          <RiDownloadLine size={16} /> Download as HTML
        </button>
        <button onClick={handlePrint} className="t-btn t-btn-primary">
          <RiPrinterLine size={16} /> Print Guidelines
        </button>
      </div>

      {/* ── Main Content ── */}
      <div className="t-guidelines-wrapper">
        <div className="t-guidelines-container">
          <div className="t-gl-grid">

            {/* Sidebar - Mobile Toggle */}
            <div>
              <button 
                className="t-sidebar-toggle"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <RiMenuLine size={16} /> Quick Navigation
                </span>
                {sidebarOpen ? <RiCloseLine size={18} /> : <RiArrowRightLine size={16} />}
              </button>

              <div className={`t-sidebar-mobile ${sidebarOpen ? "open" : ""}`}>
                <div className="t-sidebar">
                  <h3 className="t-sidebar-title">
                    <RiCheckLine size={16} style={{ color: T.primary }} />
                    Quick Navigation
                  </h3>
                  <ul className="t-sidebar-nav">
                    {sortedGuidelines.map((item, index) => (
                      <li key={index}>
                        <button
                          onClick={() => {
                            const el = document.getElementById(`guideline-${index}`);
                            if (el) {
                              el.scrollIntoView({ behavior: "smooth", block: "start" });
                              setExpandedGuideline(index);
                              if (window.innerWidth <= 768) setSidebarOpen(false);
                            }
                          }}
                          className={`t-sidebar-nav-item ${expandedGuideline === index ? "active" : ""}`}
                        >
                          <span style={{ fontSize: "clamp(14px, 1.2vw, 16px)" }}>{item.icon || "📝"}</span>
                          <span style={{ flex: 1, fontSize: "clamp(12px, 1vw, 13px)" }}>{item.title}</span>
                        </button>
                      </li>
                    ))}
                  </ul>

                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${T.borderLight}` }}>
                    <p style={{ fontSize: "clamp(10px, 0.8vw, 12px)", color: T.textMuted }}>
                      Last Updated:{" "}
                      {new Date(guidelines.updatedAt).toLocaleDateString("en-US", {
                        year: "numeric", month: "long", day: "numeric",
                      })}
                    </p>
                    <p style={{ fontSize: "clamp(10px, 0.8vw, 12px)", color: T.textMuted, marginTop: 4 }}>
                      Version: {guidelines.version || "1.0"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Cards */}
            <div>
              {sortedGuidelines.map((item, index) => (
                <motion.div
                  key={index}
                  id={`guideline-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="t-guideline-card"
                  onClick={() => setExpandedGuideline(expandedGuideline === index ? null : index)}
                >
                  <div className="t-guideline-header">
                    <div className="t-guideline-icon">{item.icon || "📝"}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                        <h3 className="t-guideline-title">{item.title}</h3>
                        <span className="t-guideline-order">#{item.order}</span>
                      </div>
                      <p className="t-guideline-desc">
                        {expandedGuideline === index
                          ? item.description
                          : `${item.description.substring(0, 120)}${item.description.length > 120 ? "..." : ""}`}
                      </p>
                    </div>
                    <RiArrowRightLine
                      size={18}
                      style={{ color: T.primaryLight, flexShrink: 0 }}
                      className={`transition-transform ${expandedGuideline === index ? "rotate-90" : ""}`}
                    />
                  </div>

                  {expandedGuideline === index && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="t-guideline-body"
                    >
                      <p style={{ fontSize: "clamp(14px, 1.2vw, 15px)", color: T.textMid, lineHeight: 1.7, marginBottom: 14 }}>
                        {item.description}
                      </p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(item.description);
                          toast.success("Guideline copied!");
                        }}
                        className="t-copy-btn"
                      >
                        Copy Guideline
                      </button>
                    </motion.div>
                  )}
                </motion.div>
              ))}

              {/* Contact card */}
              <div className="t-contact-card">
                <h3 style={{ fontSize: "clamp(18px, 1.8vw, 20px)", fontWeight: 700, color: T.white, marginBottom: 10 }}>
                  Need Help?
                </h3>
                <p style={{ color: `${T.white}90`, marginBottom: 16, fontSize: "clamp(13px, 1.1vw, 14px)" }}>
                  If you have any questions about the submission guidelines, please contact us
                </p>
                <div className="t-contact-items" style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "clamp(12px, 2vw, 24px)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, color: T.white, fontSize: "clamp(13px, 1.1vw, 14px)" }}>
                    <RiMailLine size={16} />
                    <span>submissions@meeting21.com</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, color: T.white, fontSize: "clamp(13px, 1.1vw, 14px)" }}>
                    <RiPhoneLine size={16} />
                    <span>+1 (555) 123-4567</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionGuidelines;